import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Alert,
    Box,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Typography,
    useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {AnyQuestion, QuestionType} from 'crqlar-react-common/dist/models/forms/form';
import {getQuestionTypeIcon} from '../utils/icons';
import {questionTypes} from "../utils/questionTypes";

interface MultipageQuestionsPanelProps {
    questions: AnyQuestion[];
    currentQuestionId: string;
    onQuestionSelect: (questionId: string) => void;
    onQuestionAdd: (questionType: QuestionType) => void;
    onQuestionDelete: (questionId: string) => void;
    onQuestionDuplicate: (questionId: string) => void;
    onQuestionsReorder: (questionIds: string[]) => void;
}

// Sortable Question Item Component
const SortableQuestionItem = ({
                                question,
                                index,
                                isActive,
                                onSelect,
                                onContextMenu
                            }: {
    question: AnyQuestion;
    index: number;
    isActive: boolean;
    onSelect: (questionId: string) => void;
    onContextMenu: (event: React.MouseEvent<HTMLElement>, questionId: string) => void;
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({id: question.id});

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <ListItem
            ref={setNodeRef}
            style={style}
            disablePadding
            secondaryAction={
                <IconButton
                    edge="end"
                    size="small"
                    onClick={(e) => onContextMenu(e, question.id)}
                >
                    <MoreVertIcon fontSize="small"/>
                </IconButton>
            }
            sx={{mb: 1}}
        >
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                cursor: 'grab',
                '&:active': {cursor: 'grabbing'}
            }}>
                <Box {...attributes} {...listeners} sx={{display: 'flex', alignItems: 'center'}}>
                    <DragIndicatorIcon fontSize="small" sx={{color: 'text.secondary', mr: 1}}/>
                </Box>

                <ListItemButton
                    selected={isActive}
                    onClick={() => onSelect(question.id)}
                    sx={{
                        flex: 1,
                        borderRadius: 1,
                        backgroundColor: isActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                        '&:hover': {
                            backgroundColor: isActive ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)',
                        }
                    }}
                >
                    <ListItemIcon sx={{minWidth: 40}}>
                        {getQuestionTypeIcon(question.type)}
                    </ListItemIcon>
                    <ListItemText
                        primary={`${index + 1}. ${question.title}`}
                        primaryTypographyProps={{
                            noWrap: true,
                            fontSize: '0.875rem',
                            pr: 2
                        }}
                    />
                </ListItemButton>
            </Box>
        </ListItem>
    );
};

const MultipageQuestionsPanel: React.FC<MultipageQuestionsPanelProps> = ({
                                                                         questions,
                                                                         currentQuestionId,
                                                                         onQuestionSelect,
                                                                         onQuestionAdd,
                                                                         onQuestionDelete,
                                                                         onQuestionDuplicate,
                                                                         onQuestionsReorder,
                                                                   }) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const [contextMenuAnchorEl, setContextMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null);
    const [addQuestionAnchorEl, setAddQuestionAnchorEl] = useState<null | HTMLElement>(null);

    // Check if END_SCREEN question exists
    const hasEndScreen = questions.some(q => q.type === QuestionType.END_SCREEN);

    // Configure DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Handle drag end event
    const handleDragEnd = (event: DragEndEvent) => {
        const {active, over} = event;

        if (over && active.id !== over.id) {
            const oldIndex = questions.findIndex((question) => question.id === active.id);
            const newIndex = questions.findIndex((question) => question.id === over.id);

            const reorderedQuestions = [...questions];
            const [movedQuestion] = reorderedQuestions.splice(oldIndex, 1);
            reorderedQuestions.splice(newIndex, 0, movedQuestion);

            onQuestionsReorder(reorderedQuestions.map(question => question.id));
        }
    };

    // Handle opening the context menu for a question
    const handleContextMenu = (event: React.MouseEvent<HTMLElement>, questionId: string) => {
        event.stopPropagation();
        setContextMenuAnchorEl(event.currentTarget);
        setSelectedQuestionId(questionId);
    };

    // Handle closing the context menu
    const handleContextMenuClose = () => {
        setContextMenuAnchorEl(null);
        setSelectedQuestionId(null);
    };

    // Handle opening the add question menu
    const handleAddQuestionClick = (event: React.MouseEvent<HTMLElement>) => {
        setAddQuestionAnchorEl(event.currentTarget);
    };

    // Handle closing the add question menu
    const handleAddQuestionClose = () => {
        setAddQuestionAnchorEl(null);
    };

    // Handle deleting a question
    const handleDelete = () => {
        if (selectedQuestionId) {
            onQuestionDelete(selectedQuestionId);
            handleContextMenuClose();
        }
    };

    // Handle duplicating a question
    const handleDuplicate = () => {
        if (selectedQuestionId) {
            onQuestionDuplicate(selectedQuestionId);
            handleContextMenuClose();
        }
    };

    return (
        <Box sx={{p: 2, height: '100%', display: 'flex', flexDirection: 'column'}}>
            {/* Content Section */}
            <Box sx={{mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Typography variant="subtitle1" fontWeight="bold">{t('Content')}</Typography>
                <IconButton
                    size="small"
                    onClick={handleAddQuestionClick}
                    sx={{
                        backgroundColor: theme.palette.grey[200],
                        '&:hover': {
                            backgroundColor: theme.palette.grey[300],
                        }
                    }}
                >
                    <AddIcon fontSize="small"/>
                </IconButton>
            </Box>

            {/* Questions List with Drag and Drop */}
            <Box sx={{flex: 1, overflow: 'auto'}}>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={questions.map(q => q.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        <List sx={{width: '100%', p: 0}}>
                            {questions.map((question, index) => (
                                <SortableQuestionItem
                                    key={question.id}
                                    question={question}
                                    index={index}
                                    isActive={question.id === currentQuestionId}
                                    onSelect={onQuestionSelect}
                                    onContextMenu={handleContextMenu}
                                />
                            ))}
                        </List>
                    </SortableContext>
                </DndContext>

                {/* End Screen Alert */}
                {!hasEndScreen && (
                    <Alert
                        severity="warning"
                        sx={{mt: 1, mx: 0}}
                    >
                        <Stack>
                        <Typography variant="caption">
                            {t("No end screen added. Users won't be able to submit the form.")}
                        </Typography>

                        <Button
                            color="inherit"
                            size="small"
                            variant={'outlined'}
                            onClick={() => onQuestionAdd(QuestionType.END_SCREEN)}
                            sx={{alignSelf: 'flex-start'}}
                        >
                            {t('Add End Screen')}
                        </Button>
                        </Stack>
                    </Alert>
                )}
            </Box>

            {/* Context Menu for Screen Actions */}
            <Menu
                anchorEl={contextMenuAnchorEl}
                open={Boolean(contextMenuAnchorEl)}
                onClose={handleContextMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        width: 160,
                        borderRadius: 1,
                        boxShadow: theme.shadows[3],
                    }
                }}
            >
                <MenuItem onClick={handleDelete} dense>{t('Delete')}</MenuItem>
                <MenuItem onClick={handleDuplicate} dense>{t('Duplicate')}</MenuItem>
            </Menu>

            {/* Add Question Type Menu */}
            <Menu
                anchorEl={addQuestionAnchorEl}
                open={Boolean(addQuestionAnchorEl)}
                onClose={handleAddQuestionClose}
                PaperProps={{
                    sx: {
                        width: 280,
                        maxHeight: 500,
                        borderRadius: 2,
                        boxShadow: theme.shadows[4],
                    }
                }}
            >
                {questionTypes.map((qType) => (
                    <MenuItem
                        key={qType.id}
                        onClick={() => {
                            handleAddQuestionClose();
                            onQuestionAdd(qType.type);
                        }}
                        sx={{
                            p: 2,
                            alignItems: 'flex-start',
                            whiteSpace: 'normal'
                        }}
                    >
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            minWidth: 0
                        }}>
                            <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: qType.color + '30',
                                        borderRadius: 1,
                                        mr: 2
                                    }}
                                >
                                    {getQuestionTypeIcon(qType.type)}
                                </Paper>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        whiteSpace: 'normal',
                                        overflowWrap: 'break-word',
                                        minWidth: 0
                                    }}
                                >
                                    {t(qType.name)}
                                </Typography>
                            </Box>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    pl: 7,
                                    whiteSpace: 'normal',
                                    overflowWrap: 'break-word',
                                    lineHeight: 1.35
                                }}
                            >
                                {t(qType.description)}
                            </Typography>
                        </Box>
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

export default MultipageQuestionsPanel;
